<template>
  <div class="flex h-full w-full flex-col">
    <!-- Header Section -->
    <div class="flex items-center justify-start">
      <h1 class="text-5xl font-medium text-gray-900">Your Items</h1>
    </div>

    <!-- Stock Alert -->
    <div v-if="productsWithNoStock" class="pt-8">
      <div class="flex items-center gap-2">
        <Icon
          name="assignment_late"
          type="filled"
          class="text-[#EF4444]"
        ></Icon>
        <span class="text-2xl font-medium">
          {{ __('You have') }}
          <strong
            >{{ productsWithNoStock }}
            {{ productsWithNoStock === 1 ? __('product') : __('products') }}
          </strong>
          {{ __('out of stock') }}.
        </span>
      </div>
    </div>
    <div class="flex w-full items-center justify-end py-8">
      <button
        @click="redirectToPage('redesign.company.shop.products.create')"
        class="btn btn-outline flex items-center justify-start gap-2"
      >
        <Icon name="add_box" type="filled" class="text-primary"></Icon>

        <span class="text-primary">{{ __('Add new item') }} </span>
      </button>
    </div>

    <!-- Search and Filter Section -->
    <div class="flex flex-col items-center">
      <div class="relative flex w-full items-center justify-start">
        <Input
          v-model="searchString"
          :value="searchString"
          :placeholder="__('Search Products...')"
          type="text"
          :maxlength="100"
          aria-label="Search Products"
          @input="handleSearchInput"
          icon="search"
          placeholder="Search item"
          class="w-full"
        />
      </div>

      <ProductsIndexFilterByPopup
        :filterBy="filterBy"
        @filterBy="handleFilterUpdate($event)"
      >
      </ProductsIndexFilterByPopup>
    </div>

    <!-- Table Container -->
    <div class="overflow-x-auto rounded-lg">
      <!-- Table Header -->
      <div
        class="hidden gap-4 rounded-lg bg-white px-8 py-5 text-xs font-semibold uppercase tracking-wide text-gray-700 sm:grid sm:grid-cols-6"
      >
        <div class="sm:col-span-3">PRODUCT</div>
        <div>STOCK</div>
        <div>STATE</div>
        <div class="flex items-center justify-end pr-6">ACTIONS</div>
      </div>

      <!-- Product Items -->
      <div
        class="scrollbar-thin mt-2 h-[450px] overflow-y-auto rounded-lg bg-white p-2"
        ref="scrollContainer"
      >
        <div v-if="products.data.length !== 0 && !isLoadingProducts">
          <div
            v-for="product in products.data"
            :key="product.id"
            class="grid grid-cols-5 items-center gap-4 border-b border-b-gray-200 bg-white px-6 py-4 hover:bg-gray-50 sm:grid-cols-6"
          >
            <div class="col-span-5 flex h-24 items-center gap-3 sm:col-span-3">
              <img
                :src="`/uploads/${product.image?.path}`"
                alt="Product Image"
                class="h-24 w-24 rounded-lg object-cover shadow-md transition-all duration-200 hover:shadow-lg"
              />
              <div class="0 flex h-full flex-col gap-y-4">
                <div class="font-medium text-zinc-600">{{ product.name }}</div>
                <div class="text-sm font-medium text-secondary">
                  {{
                    product.delivery_type === 'offline'
                      ? __('Offline Delivery')
                      : __('Online Delivery')
                  }}
                </div>
              </div>
            </div>

            <div class="col-span-2 flex items-center gap-2 sm:col-span-1">
              <template v-if="product.stock_status['in_stock']">
                <Icon
                  name="assignment_late"
                  type="filled"
                  class="text-[#10B981]"
                ></Icon>
                <span class="text-base font-medium">{{ __('In stock') }}</span>
              </template>

              <template v-if="product.stock_status['low_stock']">
                <Icon
                  name="assignment_late"
                  type="filled"
                  class="text-[#FFCC24]"
                ></Icon>
                <span class="text-base font-medium">{{ __('Low stock') }}</span>
              </template>

              <template v-if="product.stock_status['out_of_stock']">
                <Icon
                  name="assignment_late"
                  type="filled"
                  class="text-[#EF4444]"
                ></Icon>
                <span class="text-base font-medium">{{
                  __('Out of stock')
                }}</span>
              </template>
            </div>

            <div
              class="flex items-center justify-center gap-2 sm:justify-start"
            >
              <InputToggle
                :id="`${product.id}`"
                :value="Boolean(product.is_active)"
                @update:value="
                  handleProductStatusToggle({
                    productId: product.id,
                    is_active: !product.is_active
                  })
                "
              />
            </div>

            <div class="col-span-2 flex items-center justify-end sm:col-span-1">
              <button
                @click="
                  redirectToPage(
                    'redesign.company.shop.products.show',
                    product.id
                  )
                "
                class="btn btn-outline ring-1 ring-inset ring-primary hover:bg-primary/5"
              >
                <span class="text-primary">{{ __('Manage item') }}</span>
              </button>
            </div>
          </div>

          <div
            v-if="
              products.meta.current_page !== products.meta.last_page &&
              !isLoadingProducts
            "
            class="mt-auto flex w-full items-end justify-center py-2"
          >
            <button
              :disabled="isLoadingShowMore"
              class="hover-lift-no-shadow font-medium text-primary"
              :class="{ 'opacity-50': isLoadingShowMore }"
            >
              <div
                class="flex items-center justify-center gap-2"
                @click="
                  handlePageChange({
                    page: products.meta.current_page + 1,
                    reset: false,
                    search: searchString
                  })
                "
              >
                <span>{{ __('Show More') }}</span>
                <Icon
                  v-if="isLoadingShowMore"
                  class="animate-spin text-lg"
                  name="progress_activity"
                />
              </div>
            </button>
          </div>
        </div>

        <div v-else-if="companyHasNoProducts">
          <div :colspan="7" class="px-6 py-8 text-center text-gray-500">
            <div class="flex h-full w-full items-center justify-center">
              <div
                class="flex w-[500px] flex-col items-center justify-center pt-16"
              >
                <span class="text-2xl font-medium text-zinc-800">{{
                  __('Your Products Await!')
                }}</span>
                <span class="mt-4 text-base text-zinc-800">
                  {{
                    __(
                      "It looks like you haven't added any products  yet.\
                      This is where you'll track and manage potential products. To get started, you can add products directly from our add product page"
                    )
                  }}
                </span>
                <button
                  class="btn btn-fill hover-lift mt-8 flex w-fit items-center"
                  transition="transition-all"
                  @click.stop="
                    redirectToPage('redesign.company.shop.products.create')
                  "
                >
                  <span class="text-center">
                    {{ __('Add Products') }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex h-full w-full items-center justify-center"
          v-else-if="!isLoadingProducts && products.data.length === 0"
        >
          <span class="px-6 py-8 text-center text-gray-500">
            {{ __('No results found') }}
          </span>
        </div>

        <div
          class="flex h-full w-full items-center justify-center gap-x-2"
          v-else-if="isLoadingProducts"
        >
          <span class="text-center font-medium text-primary">
            {{ __('Loading') }}
          </span>
          <Icon
            class="animate-spin text-2xl text-primary"
            name="progress_activity"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
  import { nextTick, onMounted, onUnmounted, ref } from 'vue';
  import InputToggle from '../../../components/form/input-toggle/input-toggle.vue';
  import Input from '../../../components/global/Form/Input.vue';
  import Icon from '@/components/global/Icon.vue';
  import { useToast } from 'vue-toastification';
  import { __ } from '@/helpers';

  import ProductsIndexFilterByPopup from './components/ProductsIndexFilterByPopup.vue';

  const redirectToPage = (name, params) => {
    window.location.href = route(name, params);
  };

  const searchString = ref('');
  const toast = useToast();
  let searchTimeout = null;
  const isLoadingShowMore = ref(false);
  const isLoadingProducts = ref(false);

  onMounted(async () => {
    await nextTick();

    const storedMessage = sessionStorage.getItem('toast_message');
    if (storedMessage) {
      const { type, message } = JSON.parse(storedMessage);
      toast[type](message);
      sessionStorage.removeItem('toast_message');
    }
  });

  const props = defineProps({
    data: {
      type: Object,
      required: true
    }
  });

  const products = ref(props.data.products || {});

  const productsWithNoStock = products.value.data
    .map((product) => {
      return product.stock === 0 ? product : null;
    })
    .filter(Boolean).length;

  const companyHasNoProducts = props.data.products.data.length === 0;
  const scrollContainer = ref(null);
  const handleProductStatusToggle = async ({ productId, is_active }) => {
    try {
      const product = products.value.data.find((p) => p.id === productId);
      if (!product) return;

      const response = await axios.patch(
        route('redesign.company.shop.products.update.status', productId),
        {
          is_active: is_active,
          updated_at: product.updated_at
        }
      );

      toast.success(
        response?.data?.message || __('Product status modified successfully')
      );

      const index = products.value.data.findIndex((p) => p.id === productId);

      if (index !== -1) {
        products.value.data[index].is_active = response.data.is_active;
        products.value.data[index].updated_at = response.data.updated_at;
      }

      const savedPosition = scrollContainer.value.scrollTop;

      await handlePageChange({
        page: 1,
        reset: true
      });

      scrollContainer.value.scrollTop = savedPosition;
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    }
  };

  const filterBy = ref(null);
  const handleSearchInput = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      handlePageChange({
        page: 1,
        reset: true
      });
    }, 300);
  };

  const handlePageChange = async (eventData) => {
    isLoadingProducts.value = true;

    if (eventData.reset) {
      isLoadingProducts.value = true;
    } else {
      isLoadingShowMore.value = true;
    }

    try {
      const response = await axios.get(
        route('redesign.company.shop.products.load'),
        {
          params: {
            page: eventData.page || 1,
            reset: eventData.reset || false,
            search_string: searchString.value,
            filter: filterBy.value
          }
        }
      );

      if (eventData.reset) {
        products.value = response.data.data.products;
      } else {
        products.value.data.push(...response.data.data.products.data);
        products.value.meta.last_page =
          response.data.data.products.meta.last_page;
        products.value.meta.current_page =
          response.data.data.products.meta.current_page;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingShowMore.value = false;
      isLoadingProducts.value = false;
    }
  };

  const handleFilterUpdate = (event) => {
    filterBy.value = event;
    handlePageChange({
      reset: true,
      page: 1
    });
  };
</script>
